// What the /dailys hub shows when a submission collides with a report that
// already exists for the same date and group. The duplicate guard stays: no
// second row is ever inserted. What this adds is a way forward — say whose
// report it is, flag a form that is not dated today (the usual cause), and
// offer to update the existing report or open it, where the caller may.
import React from 'react';
import {canEditOwnRecord} from '../lib/dailyReportsApi.js';
import type {DailyReportAuthState} from '../lib/dailyReportsApi.js';
import type {DailyRecord} from '../lib/dailyDuplicateCheck.js';
import {fmtMDY} from '../lib/dateUtils.js';
import './duplicateRecovery.css';
import {canUpdateDuplicates, crossLabelDuplicateMessage, hasPhotos, isOwnReport} from './duplicateRecovery.js';
import type {DuplicateHit} from './duplicateRecovery.js';

function groupLabel(record: DailyRecord): string {
  return String(record.batch_label || record.herd || record.flock || '');
}

function describeHit(authState: DailyReportAuthState, hit: DuplicateHit): string {
  const cross = crossLabelDuplicateMessage(hit);
  if (cross) return cross;
  const label = groupLabel(hit.record);
  const what = (label ? label + ' on ' : '') + fmtMDY(String(hit.record.date || ''));
  const own = isOwnReport(authState, hit);
  const editable = canEditOwnRecord(authState, hit.existing);
  if (own && editable) return `You already submitted ${what}.`;
  if (own) return `You already submitted ${what}, and it is past the time it can be changed.`;
  if (editable) return `Another team member already submitted ${what}.`;
  return `Another team member already submitted ${what}. Ask a manager if it needs a change.`;
}

type DuplicateReportNoticeProps = {
  hits: readonly DuplicateHit[];
  authState: DailyReportAuthState;
  today: string;
  busy: boolean;
  // Offered only by the forms whose fields map onto the edit RPC one to one.
  onUpdate?: (() => void) | null;
  // Null when the caller cannot reach that program's report pages.
  pathFor: (hit: DuplicateHit) => string | null;
  onOpen: (path: string) => void;
};

export default function DuplicateReportNotice({
  hits,
  authState,
  today,
  busy,
  onUpdate,
  pathFor,
  onOpen,
}: DuplicateReportNoticeProps) {
  if (hits.length === 0) return null;
  const formDate = String(hits[0].record.date || '');
  const notToday = !!formDate && formDate !== today;
  const updatable = !!onUpdate && canUpdateDuplicates(authState, hits);
  // Photos are what stop an otherwise updatable set: say so instead of
  // silently offering nothing.
  const photosBlock =
    !!onUpdate &&
    hits.some((h) => hasPhotos(h.record)) &&
    hits.every((h) => isOwnReport(authState, h) && canEditOwnRecord(authState, h.existing));
  const plural = hits.length > 1;

  return (
    <div data-duplicate-report-notice="1" style={{fontSize: 13, color: 'var(--ink)', margin: '-4px 0 12px'}}>
      {notToday && (
        <div data-duplicate-not-today="1" style={{fontWeight: 600, marginBottom: 6}}>
          This form is dated {fmtMDY(formDate)}, not today ({fmtMDY(today)}). If this is today’s report, change the date
          above and submit again.
        </div>
      )}
      {hits.map((h) => (
        <div key={h.existing.id} style={{marginBottom: 4}}>
          {describeHit(authState, h)}
        </div>
      ))}
      {photosBlock && (
        <div className="wcf-dup-help" style={{marginBottom: 4}}>
          To update {plural ? 'those reports' : 'that report'} from here, remove the photos first.
        </div>
      )}
      <div style={{display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8}}>
        {updatable && (
          <button
            type="button"
            data-duplicate-update="1"
            disabled={busy}
            onClick={() => onUpdate?.()}
            className="wcf-dup-btn"
          >
            {busy ? 'Saving…' : plural ? 'Update Those Reports' : 'Update That Report'}
          </button>
        )}
        {hits.map((h) => {
          const path = pathFor(h);
          if (!path) return null;
          const label = groupLabel(h.existing) || groupLabel(h.record);
          return (
            <button
              key={h.existing.id}
              type="button"
              data-duplicate-open={h.existing.id}
              disabled={busy}
              onClick={() => onOpen(path)}
              className="wcf-dup-btn"
            >
              {label ? `Open ${label} Report` : 'Open That Report'}
            </button>
          );
        })}
      </div>
    </div>
  );
}
